import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
    Paper, Grid, Typography, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, TextField, MenuItem, Button, CircularProgress, Chip, IconButton, Zoom 
} from '@material-ui/core/';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import SaveIcon from '@material-ui/icons/Save';
import LockIcon from '@material-ui/icons/Lock';
import { authFetch, logout } from '../../auth';

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(3), width: "100%" },
    title: { backgroundColor: theme.palette.primary.main, color: '#fff', padding: theme.spacing(1), borderRadius: 4, marginBottom: 15 },
    tableHeader: { backgroundColor: theme.palette.secondary.light, fontWeight: 'bold', color: theme.palette.primary.main },
    chipContainer: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.5), marginTop: theme.spacing(1) },
    inputInline: { width: 150, marginRight: theme.spacing(1) },
    actionRow: { marginTop: theme.spacing(3), display: 'flex', justifyContent: 'flex-end' },
    formRow: { width: "100%", maxHeight: "50%" },
    visibleChip: {
        color: theme.palette.primary.dark, // Color oscuro del tema (ej. Azul/Negro)
        fontWeight: 500,
        borderColor: theme.palette.primary.main,
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        '& .MuiChip-deleteIcon': {
            color: theme.palette.primary.main, // Icono de la 'X' también visible
        },
    },
    lockedChip: {
        color: '#757575', 
        borderColor: '#bdbdbd',
        backgroundColor: '#f5f5f5',
        fontWeight: 'normal'
    },
    formText: { fontSize: 14, color: theme.palette.primary.main, textAlign: "center" },
    lockNotice: {
        backgroundColor: '#fff3e0',
        color: '#b78103',
        padding: theme.spacing(1.5),
        borderRadius: 4,
        marginBottom: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold'
    }
}));

export default function HesManager(props) {
    const classes = useStyles();
    const billingDate = props.billingDate;

    // Estados de Ciclos
    const [cycles, setCycles] = useState([]);
    const [cycleUsers, setCycleUsers] = useState([]);
    const [selectedCycle, setSelectedCycle] = useState("");

    // 🔒 ESTADO DE BLOQUEO (Facturación con HES ya ejecutada)
    const [isCycleLocked, setIsCycleLocked] = useState(false);

    // Estados de datos de facturas y carga
    const [bills, setBills] = useState([]); 
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // 1. Carga inicial de ciclos y usuarios activos
    useEffect(() => {
        authFetch("/api/check-billing-cycles?username=" + String(props.username)).then(response => {
            if (response.status === 422) { logout(); return null; }
            return response.json();
        }).then(response => {
            if (response && response.cycles) { setCycles(response.cycles); }
        });

        authFetch("/api/get-cycle-users?username=" + String(props.username) + "&billing=true").then(response => {
            if (response.status === 422) { logout(); return null; }
            return response.json();
        }).then(response => {
            if (response && response.cycle_users) { setCycleUsers(response.cycle_users); }
        });
    }, [props.username]);

    // 2. Carga de facturas y verificación de parámetros de cierre (Flags)
    useEffect(() => {
        if (selectedCycle === "") {
            setBills([]);
            setIsCycleLocked(false);
            return;
        }
        setLoading(true);
        
        const year = billingDate.getFullYear();
        const month = billingDate.getMonth() + 1;
        
        // 💥 PRIMER FETCH: Verificar si el ciclo ya se encuentra cerrado/generado con HES
        let opts = {
            users_by_cycle: cycleUsers,
            year: year,
            month: String(month)
        };

        authFetch(`/api/check-billing-generation-params?username=${props.username}&year=${year}&month=${month}&version=${props.tariffCalcVersion}`, {
            method: 'POST',
            body: JSON.stringify(opts)
        })
        .then(res => res.json())
        .then(flagsResponse => {
            // Evaluamos si el flag de generación con HES ya está activo para este ciclo
            if (flagsResponse && flagsResponse.bill_generated_with_hes_flags) {
                const lockedStatus = flagsResponse.bill_generated_with_hes_flags[selectedCycle] || false;
                setIsCycleLocked(lockedStatus);
            }

            // SEGUNDO FETCH: Traer las filas de liquidación correspondientes
            return authFetch(`/api/get-cycle-bill-numbers?username=${props.username}&year=${year}&month=${month}&cycle=${selectedCycle}&version=${props.tariffCalcVersion}`);
        })
        .then(res => {
            if (res && res.status === 422) { logout(); return null; }
            return res ? res.json() : null;
        })
        .then(data => {
            if (data && data.bills) {
                const initializedBills = data.bills.map(b => ({ ...b, currentInput: "" }));
                setBills(initializedBills);
            }
            setLoading(false);
        })
        .catch(() => setLoading(false));

    }, [billingDate, selectedCycle, props.tariffCalcVersion, props.username, cycleUsers]);

    // Control de inputs locales por fila
    const handleInputChange = (index, value) => {
        if (isCycleLocked) return; // Cláusula de seguridad
        const updated = [...bills];
        updated[index].currentInput = value;
        setBills(updated);
    };

    // Insertar pildora HES
    const addHesToken = (index) => {
        if (isCycleLocked) return;
        const updated = [...bills];
        const token = updated[index].currentInput.trim();
        
        if (token && !updated[index].hes.includes(token)) {
            updated[index].hes.push(token);
            updated[index].currentInput = ""; 
            setBills(updated);
        }
    };

    // Remover pildora HES
    const removeHesToken = (billIndex, tokenIndex) => {
        if (isCycleLocked) return;
        const updated = [...bills];
        updated[billIndex].hes.splice(tokenIndex, 1);
        setBills(updated);
    };

    // Validación del formulario
    const isFormValid = useMemo(() => {
        if (bills.length === 0 || isCycleLocked) return false;
        return bills.every(bill => bill.hes && bill.hes.length > 0);
    }, [bills, isCycleLocked]);

    // Envío masivo
    const handleSubmitHes = () => {
        if (isCycleLocked) return;
        setSubmitting(true);
        const year = billingDate.getFullYear();
        const month = billingDate.getMonth() + 1;

        const payload = {
            bills: bills.map(b => ({ bill_number: b.bill_number, hes: b.hes }))
        };

        authFetch(`/api/update-cycle-hes?username=${props.username}&year=${year}&month=${month}&cycle=${selectedCycle}&version=${props.tariffCalcVersion}`, {
            method: 'POST',
            body: JSON.stringify(payload)
        })
        .then(res => res.json())
        .then(data => {
            setSubmitting(false);
            if (data.status === 'OK') {
                alert("Autorizaciones cargadas exitosamente.");
                setIsCycleLocked(true); // Bloqueamos inmediatamente el componente tras guardar con éxito
                if (props.onSuccess) props.onSuccess(); 
            } else {
                alert("Error: " + data.error);
            }
        })
        .catch(() => setSubmitting(false));
    };

    return (
        <Zoom in timeout={500}>
            <Paper className={classes.root} elevation={2}>
                <Typography variant="subtitle1" align="center" className={classes.title}>
                    Asignación Masiva de Autorizaciones Contables (HES)
                </Typography>

                {/* Selector de Ciclo */}
                <Grid container justify="center" style={{ marginBottom: 25 }}>
                    <Grid item xs={3}>
                        <Typography variant="subtitle2" align="center" color="primary">Ciclo a gestionar:</Typography>
                        <TextField 
                            select
                            variant="outlined" 
                            size="small"
                            InputProps={{ classes: { input: classes.formText } }}
                            className={classes.formRow} 
                            value={selectedCycle}
                            onChange={(e) => setSelectedCycle(e.target.value)}
                        >
                            {cycles.map((cycle, idx) => (
                                cycleUsers[cycle.code] > 0 ?
                                <MenuItem key={idx} value={cycle.code}>{cycle.code}</MenuItem>
                                : null
                            ))}
                        </TextField>
                    </Grid>
                </Grid>

                {/* 🔒 AVISO VISUAL DE CICLO CERRADO / BLOQUEADO */}
                {isCycleLocked && (
                    <div className={classes.lockNotice}>
                        <LockIcon style={{ marginRight: 8 }} />
                        LECTURA EXCLUSIVA: La facturación con HES para el ciclo {selectedCycle} ya fue generada. No se permiten modificaciones.
                    </div>
                )}

                {/* Renderizado principal */}
                {selectedCycle === "" ? (
                    <Typography variant="body2" align="center" color="textSecondary">
                        Por favor, seleccione un ciclo para listar las facturas disponibles.
                    </Typography>
                ) : loading ? (
                    <Grid container justify="center"><CircularProgress /></Grid>
                ) : bills.length === 0 ? (
                    <Typography variant="body2" align="center" color="textSecondary">
                        No se encontraron facturas generadas (con número asignado) para este ciclo en el periodo actual.
                    </Typography>
                ) : (
                    <Fragment>
                        <TableContainer style={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.tableHeader} width="30%">Número de Factura</TableCell>
                                        <TableCell className={classes.tableHeader} width="70%">Códigos Autorizados (HES)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {bills.map((row, idx) => (
                                        <TableRow key={row.bill_number} hover>
                                            <TableCell style={{ fontWeight: 'bold'}}>
                                                {row.bill_number}
                                            </TableCell>
                                            <TableCell>
                                                {/* 💥 Ocultamos la barra de inserción si el ciclo está cerrado */}
                                                {!isCycleLocked && (
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <TextField
                                                            className={classes.inputInline}
                                                            placeholder="Ej: 11212433"
                                                            size="small"
                                                            value={row.currentInput || ""}
                                                            onChange={(e) => handleInputChange(idx, e.target.value)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHesToken(idx); } }}
                                                        />
                                                        <IconButton color="primary" onClick={() => addHesToken(idx)} size="small">
                                                            <AddCircleIcon />
                                                        </IconButton>
                                                    </div>
                                                )}

                                                <div className={classes.chipContainer}>
                                                    {row.hes.map((token, tokenIdx) => (
                                                        <Chip
                                                            key={tokenIdx}
                                                            label={token}
                                                            // Si está bloqueado, removemos la prop onDelete (desaparece la 'X')
                                                            onDelete={isCycleLocked ? undefined : () => removeHesToken(idx, tokenIdx)}
                                                            // 💥 Aplicamos las nuevas clases de estilos visuales corregidos
                                                            className={isCycleLocked ? classes.lockedChip : classes.visibleChip}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <div className={classes.actionRow}>
                            {submitting ? <CircularProgress size={28} /> : (
                                <Button
                                    variant="contained"
                                    color={isCycleLocked ? "default" : "secondary"}
                                    startIcon={isCycleLocked ? <LockIcon /> : <SaveIcon />}
                                    disabled={!isFormValid || isCycleLocked} // Bloqueado si el formulario no es válido o si isCycleLocked es true
                                    onClick={handleSubmitHes}
                                >
                                    {isCycleLocked ? "Ciclo Cerrado" : "Cargar Autorizaciones y Activar Ciclo"}
                                </Button>
                            )}
                        </div>
                    </Fragment>
                )}
            </Paper>
        </Zoom>
    );
}

